import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import PropTypes from 'prop-types';
import TableHeader from 'components/Table/TableHeader';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initSummary = {
  quantity: '',
  amount: '',
  discount: '',
  total_discount: '',
  net: '',
};

function QuerySaleOrderList({ productCode, setLoading }) {
  const mod = 'rp_product_query';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [summary, setSummary] = useState(initSummary);
  const closeShow = () => setShow(null);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);

  const initHeader = [{ id: 'rs.branch_id', label: 'สาขา', align: 'center' }];
  for (let i = 0; i <= 11; i += 1) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    initHeader.push({
      id: `m${i}`,
      label: `${m}/${y}`,
      align: 'right',
    });
  }
  initHeader.push({ id: 'year', label: 'รวม12เดือน', align: 'right' });
  initHeader.push({ id: 'unit_name', label: 'หน่วย', align: 'center' });

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('OPTIONS', `/${mod}/saleorder_list`, app.token, {
      lang: app.lang,
      search: data.search,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (productCode) view({ ...find, search: productCode, page: 1 });
  }, [productCode]);

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderList = () => {
    return (
      <TableContainer component={Paper} sx={{ minHeight: 400 }}>
        <Table aria-label='datatable table'>
          <TableHeader
            order={order}
            orderBy={orderBy}
            onRequestSort={handleSort}
            data={initHeader}
          />
          <TableBody>
            {record.length === 0 || !productCode ? (
              <TableRow>
                <TableCell colSpan={initHeader.length} align='center'>
                  ไม่พบข้อมูล
                </TableCell>
              </TableRow>
            ) : (
              record.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell align='center'>
                    <MDBox className='nowrap'>{row.branch_name}</MDBox>
                  </TableCell>
                  {Array.from({ length: 12 }, (_, i) => (
                    <TableCell align='right' className='nowrap'>
                      <MDBox className='nowrap'>{Func.numberDisplay(row[`m${i}`])}</MDBox>
                    </TableCell>
                  ))}
                  <TableCell className='nowrap highlight-success'>
                    <MDBox className='nowrap' align='right'>
                      {Func.numberDisplay(row.year)}
                    </MDBox>
                  </TableCell>
                  <TableCell align='center'>
                    <MDBox className='nowrap'>{row.unit_name}</MDBox>
                  </TableCell>
                </TableRow>
              ))
            )}
            {record.length !== 0 && (
              <TableRow>
                <TableCell align='right' colSpan={1}>
                  รวม
                </TableCell>
                {Array.from({ length: 12 }, (_, i) => (
                  <TableCell align='right' colSpan={1}>
                    {Func.numberDisplay(summary[`total_m${i}`])}
                  </TableCell>
                ))}
                <TableCell align='right' className='highlight-success'>
                  {Func.numberDisplay(summary.total_year)}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return <>{renderList()}</>;
}

QuerySaleOrderList.propTypes = {
  productCode: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
};

export default QuerySaleOrderList;
